import React from 'react';
import { connect } from 'react-redux';
import UserProfile from '../UserProfile';
import PhotographersSidebarNavItem from '../PhotographersSidebarNavItem';
import MaintenanceModeToggle from '../MaintenanceModeToggle';
import AdminAlertsSidebarItem from '../AdminAlertsSidebarItem';
import NewOrderAlertSound from '../NewOrderAlertSound';
import { isMainAdmin } from '../../utils/isMainAdmin';

/**
 * Shared sidebar + topbar chrome for every Admin page — replaces each routed
 * page duplicating its own full `<nav className="sidebar">` markup.
 *
 * Not a React Router layout route (no <Outlet/>): this app navigates via
 * plain `<a href>` full page reloads on purpose (see App.tsx's comment on
 * why — "animations work if the page is being mounted through native
 * redirection"), so a nested-route shell would remount on every navigation
 * anyway and buys nothing. This is a plain wrapper component instead —
 * same result (one shared sidebar, no duplicated markup) without touching
 * the existing navigation model.
 *
 * Sidebar grouping follows admin-dashboard.html's structure (Overview /
 * Operations / People / Business / Marketing / Analytics / Admin), adapted
 * to this app's real pages — see the redesign plan for the page-by-page
 * mapping. `activePage` matches one of NAV_ITEMS' `key` values below.
 */

type NavLink = {
    key: string;
    label: string;
    href: string;
    icon: string;
    mainAdminOnly?: boolean;
    soon?: boolean;
};

type NavGroup = {
    label: string;
    items: NavLink[];
};

const NAV_GROUPS: NavGroup[] = [
    {
        label: 'Overview',
        items: [
            { key: 'home', label: 'Dashboard', href: '/dashboard', icon: 'mdi-speedometer' },
            { key: 'live-map', label: 'Live map', href: '/live-map', icon: 'mdi-map-marker-radius' },
        ],
    },
    {
        label: 'Operations',
        items: [
            { key: 'orders', label: 'Orders', href: '/orders', icon: 'mdi-playlist-play' },
            { key: 'availability', label: 'Availability', href: '/availability', icon: 'mdi-clock-outline' },
            { key: 'service-blocking', label: 'Service Blocking', href: '/service-blocking', icon: 'mdi-calendar-remove' },
        ],
    },
    {
        label: 'People',
        items: [
            { key: 'photographers', label: 'Photographers', href: '/photographers', icon: 'mdi-account-tie-outline' },
            { key: 'pending-requests', label: 'Pending Requests', href: '/pending-requests', icon: 'mdi-account-clock-outline' },
            { key: 'customers', label: 'Customers', href: '/customers', icon: 'mdi-account-outline' },
        ],
    },
    {
        label: 'Business',
        items: [
            { key: 'services', label: 'Services', href: '/services', icon: 'mdi-camera-outline' },
            { key: 'discounts', label: 'Discounts', href: '/discounts', icon: 'mdi-ticket-percent-outline' },
            { key: 'payments', label: 'Payments', href: '/payments', icon: 'mdi-cash-multiple' },
        ],
    },
    {
        label: 'Marketing',
        items: [
            { key: 'notify', label: 'Send notification', href: '/notify', icon: 'mdi-bell-ring-outline' },
            { key: 'portfolio', label: 'Portfolio', href: '/portfolio', icon: 'mdi-image-multiple-outline' },
            { key: 'content', label: 'App Content', href: '/content', icon: 'mdi-file-document-edit-outline' },
        ],
    },
    {
        label: 'Analytics',
        items: [
            { key: 'analytics', label: 'Analytics', href: '/analytics', icon: 'mdi-chart-line' },
            { key: 'reviews', label: 'Reviews', href: '/reviews', icon: 'mdi-star-outline' },
            { key: 'customer-ratings', label: 'Customer Ratings', href: '/customer-ratings', icon: 'mdi-star-outline' },
        ],
    },
    {
        label: 'Admin',
        items: [
            { key: 'admins', label: 'Admins & Roles', href: '/admins', icon: 'mdi-account-key-outline', mainAdminOnly: true },
        ],
    },
];

type Props = {
    activePage: string;
    title: string;
    subtitle?: string;
    role?: string;
    topbarActions?: React.ReactNode;
    children?: React.ReactNode;
};

class AdminShell extends React.Component<Props> {
    render(): React.ReactNode {
        const { activePage, title, subtitle, role, topbarActions, children } = this.props;
        const mainAdmin = isMainAdmin(role);

        return (
            <div className="container-scroller">
                <nav className="sidebar sidebar-offcanvas" id="sidebar">
                    <ul className="nav">
                        <UserProfile />
                        <NewOrderAlertSound />
                        {/* Client-reported: "remove the alerts from sidebar not required
                            just hide for now" — kept mounted-but-commented (not deleted)
                            so it's a one-line restore if it's wanted back later. The bell-
                            ring sound (NewOrderAlertSound, above) is unrelated and stays on. */}
                        {/* <AdminAlertsSidebarItem /> */}
                        <li className="nav-item nav-category">
                            <span className="nav-link">Navigation</span>
                        </li>
                        {NAV_GROUPS.map((group) => {
                            const visibleItems = group.items.filter((item) => !item.mainAdminOnly || mainAdmin);
                            if (visibleItems.length === 0) return null;
                            return (
                                <React.Fragment key={group.label}>
                                    <li className="nav-item nav-category">
                                        <span className="nav-link">{group.label}</span>
                                    </li>
                                    {visibleItems.map((item) => (
                                        // Photographers carries a "needs service assignment" count
                                        // badge — reuse the existing component (it fetches/polls its
                                        // own count) instead of the plain link, right in its normal
                                        // spot in the group instead of a duplicate floating above it.
                                        item.key === 'photographers' ? (
                                            <PhotographersSidebarNavItem key={item.key} />
                                        ) : (
                                        <li className="nav-item menu-items" key={item.key}>
                                            <a
                                                className={`nav-link${activePage === item.key ? ' active' : ''}`}
                                                href={item.href}
                                            >
                                                <span className="menu-icon">
                                                    <i className={`mdi ${item.icon}`}></i>
                                                </span>
                                                <span className="menu-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    {item.label}
                                                    {item.soon ? (
                                                        <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '.04em', background: 'rgba(255,255,255,.08)', padding: '2px 6px', borderRadius: 6 }}>
                                                            SOON
                                                        </span>
                                                    ) : null}
                                                </span>
                                            </a>
                                        </li>
                                        )
                                    ))}
                                </React.Fragment>
                            );
                        })}
                        {/* Last item in the sidebar, after the Admin group — client
                            request: was floating awkwardly at the very top before. */}
                        <MaintenanceModeToggle />
                    </ul>
                </nav>
                <div className="container-fluid page-body-wrapper">
                    <div className="main-panel">
                        <div className="content-wrapper">
                            <div className="admin-topbar" style={{
                                display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
                                marginBottom: 22, paddingBottom: 14, borderBottom: '1px solid rgba(255,255,255,0.08)',
                            }}>
                                <div>
                                    <h3 style={{ margin: 0, fontWeight: 800 }}>{title}</h3>
                                    {subtitle ? (
                                        <div style={{ fontSize: 12.5, opacity: 0.65, fontWeight: 600, marginTop: 3 }}>{subtitle}</div>
                                    ) : null}
                                </div>
                                {topbarActions ? <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>{topbarActions}</div> : null}
                            </div>
                            {children}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state: any) => ({
    role: state.auth?.role,
});

export default connect(mapStateToProps)(AdminShell);
